import { HealthcareCompany } from "@shared/types/commonTypes";
import { Tone } from "@medentic/polaris-react/build/ts/src/components/Badge";

export const formatDate = (date: string, locale = "en-US") => {
  const l = locale == "en" ? "en-US" : locale;
  return date ? new Intl.DateTimeFormat(l).format(new Date(date)) : "";
};

/**
 * Extracts the clinic ID from the provided path and finds the corresponding clinic from the list of healthcare companies.
 *
 * @param clinics - An array of healthcare companies to search from.
 * @param path - The path from which to extract the clinic ID.
 * @returns The healthcare company that matches the extracted clinic ID, or null if not found.
 */
export const findClinicByPath = (
  clinics: HealthcareCompany[],
  path?: string
): HealthcareCompany | null => {
  // Match the clinic ID pattern in the path
  const clinicIdMatch = path?.match(/\/c\/([a-f0-9-]{36})\//);
  if (!clinicIdMatch) {
    return null;
  }

  // Extract the clinic ID
  const clinicId = clinicIdMatch[1];
  // Find the clinic in the array by ID
  return clinics.find((clinic) => clinic.id === clinicId) || null;
};

export const getBadgeTone = (status: string): Tone | undefined => {
  switch (status?.toLocaleLowerCase()) {
    case "active":
    case "accepted":
      return "success";
    case "expired":
      return "attention";
    default:
      return undefined;
  }
};

/**
 * Extracts the event organizer ID from the provided path and finds the corresponding healthcare company from the list.
 *
 * @param eventOrganizers - An array of healthcare companies to search from.
 * @param path - The path from which to extract the event organizer ID.
 * @returns The healthcare company that matches the extracted ID, or null if not found.
 */
export const findEventOrganizerByPath = (
  eventOrganizers: HealthcareCompany[],
  path?: string
): HealthcareCompany | null => {
  // Match the event organizer ID pattern in the path
  const eventOrganizerIdMatch = path?.match(/\/e\/([a-f0-9-]{36})\//);
  if (!eventOrganizerIdMatch) {
    return null;
  }

  // Extract the event organizer ID
  const eventOrganizerId = eventOrganizerIdMatch[1];
  // Find the event organizer in the array by ID
  return (
    eventOrganizers.find(
      (eventOrganizer) => eventOrganizer.id === eventOrganizerId
    ) || null
  );
};

export function formatCurrency(
  value: number,
  currencyIso: string,
  currencies: Array<{
    iso: string;
    name: string;
    symbol: string;
    format: string;
    valueFormatLocale: string;
  }>,
  decimalPlaces: number = 2
) {
  const currency = currencies.find((c) => c.iso === currencyIso);
  if (!currency) return value.toFixed(decimalPlaces);
  if (currency.valueFormatLocale) {
    return new Intl.NumberFormat(currency.valueFormatLocale, {
      style: "currency",
      currency: currency.iso,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value);
  }
  return currency.format.replace("{{value}}", value.toFixed(decimalPlaces));
}

export function formatStartDate(dateStart?: string) {
  if (!dateStart) return "";
  const start = new Date(dateStart);
  const options: Intl.DateTimeFormatOptions = { day: "2-digit" };
  const startStr = start.toLocaleDateString(undefined, options);
  return startStr;
}
